import _ from 'lodash'
import {
  isEqual,
  isEmpty,
  reject,
  filter,
  has,
  map,
  flatMap,
  flow,
  find,
  isString,
  isNil,
  getOr,
  omit,
  get,
} from 'lodash/fp'
import MobileDetect from 'mobile-detect'
import { delay } from 'redux-saga'
import { select, call, all, put, take, takeLatest, takeEvery, race } from 'redux-saga/effects'
import { v1 as generateUUID } from 'uuid'

import { getAppConfig } from 'deploy-env/app-config'
import {
  previewPhoto,
  removeFile,
  signDocument,
  showErrorDialog,
  showSignatureDialog,
  showPreviewPDFDialog,
  showPreviewMultiplePDFDialog,
  removeError,
  addError,
  setAppValue,
  addFile,
  addFileProps,
  showLoading,
  hideLoading,
  hideDialog,
  saveAppData,
  getAIUWStatus,
} from 'e-submission/apps/actions'
import { getFiles, getCurrentApp, getCurrentValue } from 'e-submission/apps/selectors'
import applicationCordovaWrapper from 'e-submission/domain/data/application-cordova-wrapper'
import { APP_STATUS, DOCUMENT_TYPE, GET_DOCUMENTS_ERROR } from 'e-submission/domain/data-model/constants'
import {
  isAcknowledge,
  getReceipts,
  isReceiptSigned,
  isAdditionalDocument,
  isCreditCardRelationshipDocument,
  getAppStatus,
  getReceiptKey,
  getReceiptFromDocument,
  getReceiptNo,
  isReceipt,
  getPolicyId,
  listSignature,
  getPaidReceipts,
  hasReceiptPaid,
  isDaRelationshipDocument,
  isFinishedLegacyDcaWithPayer,
  isPaymentSlipDocument,
} from 'e-submission/domain/data-model/form/selectors'
import { isCordova, base64ToBlob } from 'e-submission/utils'
import { AVAILABLE_IMAGE_FORMATS, AVAILABLE_FILE_FORMATS } from 'e-submission/utils/pattern'
import createSignPad from 'e-submission/utils/sign-pad'
import { getIdentityUser } from 'identity/selectors'
import { getToggles } from 'quick-quote/feature-toggles'
import { setNotificationMessage } from 'quick-quote/notification/actions'
import { updateOptyStagePayment } from 'quick-quote/opty/actions'

const mapWithKey = map.convert({ cap: false })

export const uploadAttachment = function*(service, attachment) {
  const { setAttachment, getApplicationRev, deleteAdditionalDocument } = service
  const { name, file, isInsured } = attachment

  const user = yield select(getIdentityUser)
  const app = yield select(getCurrentApp)

  // save app data before upload in order to get the correct application value
  if (getToggles().ENABLE_EDA) {
    yield put(saveAppData())

    const { failed } = yield race({
      success: take('SAVE_APPLICATION_SUCCESS'),
      failed: take('SAVE_APPLICATION_FAIL'),
    })

    if (failed) {
      const notificationMessage = getToggles().ENABLE_RED_TRUCK_ERROR_MSG
        ? { type: 'GenericError', message: 'การอัพโหลดเอกสารมีปัญหา' }
        : { type: 'GenericError' }

      yield put(setNotificationMessage(notificationMessage))

      const files = yield select(getFiles)
      const policyId = getPolicyId(app)
      const docType = 'additional-doc'
      const { documentId } = get(docType, files)

      if (isAdditionalDocument(name)) {
        yield call(
          deleteAdditionalDocument,
          user,
          {
            documentId,
            policyId,
          },
          docType
        )

        yield put(removeFile(docType))
      }
    }
  }

  while (true) {
    try {
      const _rev = yield call(getApplicationRev, app, user)
      const newApp = {
        ...app,
        _rev,
      }
      yield call(setAttachment, newApp, name, file, user, isInsured)
      yield put({ type: 'UPLOAD_ATTACHMENT_SUCCESS' })

      break
    } catch (err) {
      if (get('response.status', err) !== 409) {
        throw err
      }
    }
  }
}

export const onListDocument = function*(service, action) {
  const { listDocument } = service
  const isFetch = !isNil(action)
  const app = yield select(getCurrentApp)
  let policyId = getPolicyId(app)
  if (!policyId) return []
  let documentList = []

  try {
    if (isFetch) {
      yield put.resolve(showLoading())
    }

    const user = yield select(getIdentityUser)
    const signatures = listSignature(app)
    yield map(({ key }) => put(removeFile(key)), signatures)
    const insuredDocs = get('signedDocs.insured', app) || []
    const acknowledgeDocs = get('signedDocs.acknowledge', app) || []
    const shouldAddEcbr = insuredDocs.length > 0 && acknowledgeDocs.length > 0
    if (getAppStatus(app) === APP_STATUS.SIGNED || shouldAddEcbr) {
      const getECBRKey = (receiptNo) =>
        flow(getReceiptKey(receiptNo), (key) => {
          return `ECBR_${key}`
        })(app)

      yield flow(
        getReceipts,
        filter(isReceiptSigned),
        map(({ receiptNo }) => put(addFile(getECBRKey(receiptNo), true)))
      )(app)
    }

    const documents = yield call(listDocument, user, policyId)

    const isLegacyDcaWithPayer = isFinishedLegacyDcaWithPayer(app)

    const patchDocuments = documents.map((doc) => {
      if (doc.code === 'dca' && isLegacyDcaWithPayer) {
        return {
          ...doc,
          signature: {
            payer: true,
          },
        }
      } else {
        return doc
      }
    })

    const getKey = (path) => flow(find(path), get('key'))(signatures)

    yield flow(
      reject(flow(get('signature'), isEmpty)),
      flatMap(({ code, signature }) =>
        mapWithKey((value, signedBy) => put(addFile(getKey({ signedBy, documentName: code }), true)))(signature)
      )
    )(patchDocuments)
    const addionalAndCreditCardRelationshipDocuments = patchDocuments.filter(
      ({ documentType }) => isAdditionalDocument(documentType) || isCreditCardRelationshipDocument(documentType)
    )
    yield flow(
      map(({ documentType, documentId, description, creationdate, isInsured }) =>
        put(
          addFileProps(`${documentType}_${documentId}`, {
            documentId,
            description,
            creationdate: new Date(creationdate).valueOf(),
            isInsured,
          })
        )
      )
    )(addionalAndCreditCardRelationshipDocuments)

    documentList = patchDocuments
  } catch (err) {
    console.error(err)
  } finally {
    if (isFetch) {
      yield put(hideLoading())
    }
  }

  return documentList
}

/* istanbul ignore next */
const isMobile = {
  Android: () => {
    return navigator.userAgent.match(/Android/i)
  },
  BlackBerry: () => {
    return navigator.userAgent.match(/BlackBerry/i)
  },
  iOS: () => {
    return navigator.userAgent.match(/iPhone|iPad|iPod/i)
  },
  Opera: () => {
    return navigator.userAgent.match(/Opera Mini/i)
  },
  Windows: () => {
    return navigator.userAgent.match(/IEMobile/i) || navigator.userAgent.match(/WPDesktop/i)
  },
  any: () => {
    return isMobile.Android() || isMobile.BlackBerry() || isMobile.iOS() || isMobile.Opera() || isMobile.Windows()
  },
}

export const onShowSignPad = function*(service, action) {
  const document = action.payload

  const app = yield select(getCurrentApp)
  const person = document.sectionId

  const mobileDetect = new MobileDetect(window.navigator.userAgent)

  if (isAcknowledge(person)) {
    // Mock sigature with one pixel image
    yield call(onSignEmptySignatureDocument, service, document)
    if (getToggles().ENABLE_CITIZEN_ID_CONSENT_FORM) {
      const docsName = document.allDocuments.map((d) => d.name)
      yield [put(setAppValue('signedDocs.acknowledge', docsName)), take('SAVE_APPLICATION_SUCCESS')]
    }
    return
  }

  if (isCordova || getToggles().ENABLE_MOUSE_SIGNING || mobileDetect.mobile()) {
    yield put(showSignatureDialog(document))
  } else {
    console.info('Not on Mobile Device, WAITING FOR SIGNPAD...')
    yield put.resolve(showLoading())
    const { waitSign, destroy } = createSignPad()

    try {
      const params = {
        firstName: get([person, 'firstName'], app),
        lastName: get([person, 'lastName'], app),
      }

      const { base64 } = yield race({
        base64: call(waitSign, params),
        cancelled: take('HIDE_DIALOG'),
      })

      if (base64) {
        yield put(
          signDocument({
            ...document,
            data: base64,
          })
        )
      }
    } catch (err) {
      console.error(err)
    } finally {
      destroy()
      yield put(hideLoading())
      yield put(hideDialog())
    }
  }
}

export const onSignDocument = function*(service, action) {
  const { signDocument, signAllDocument, signPayment, postPayment } = service
  const user = yield select(getIdentityUser)

  try {
    yield put.resolve(showLoading())

    let document = action.payload
    const app = yield select(getCurrentApp)

    if (isReceipt(document)) {
      const receipt = getReceiptFromDocument(app, document)
      const receiptNo = getReceiptNo(receipt)
      const payment = {
        ...document,
        receiptNo,
      }

      let c = 0
      while (c < 5) {
        yield delay(1000)
        yield put.resolve(showLoading())
        c++
      }
      if (!_.isEmpty(receiptNo)) {
        yield call(signPayment, user, payment)
      } else {
        yield call(postPayment, user, payment)
      }

      const receipts = getReceipts(app)

      const receiptsWithSign = receipts.map((val) =>
        isEqual(receiptNo)(getReceiptNo(val)) ? { ...val, signed: true } : val
      )
      yield put(setAppValue('receipts', receiptsWithSign))

      if (receiptsWithSign && receiptsWithSign.length > 0) {
        yield put(updateOptyStagePayment())
        yield put(getAIUWStatus())
      }
    } else if (document.allDocuments && document.allDocuments.length > 0) {
      // check if any document in document.allDocuments is a receipt
      const receipts = document.allDocuments.filter(isReceipt)
      if (receipts.length) {
        const receipt = getReceiptFromDocument(app, document)
        const receiptNo = getReceiptNo(receipt)
        const payment = {
          ...document,
          receiptNo,
        }

        yield delay(5000)
        if (!_.isEmpty(receiptNo)) {
          yield call(signPayment, user, payment)
        } else {
          yield call(postPayment, user, payment)
        }

        const receipts = getReceipts(app)

        const receiptsWithSign = receipts.map((val) =>
          isEqual(receiptNo)(getReceiptNo(val)) ? { ...val, signed: true } : val
        )
        yield put(setAppValue('receipts', receiptsWithSign))
      }
      const docsName = _.compact(
        document.allDocuments.map((d) => {
          if (!isReceipt(d)) return d.name
        })
      )
      // if paid and remoteSelling document.signature.signedBy = advisor
      const isPaid = hasReceiptPaid(app)
      yield call(
        signAllDocument,
        user,
        {
          ...document,
          signature: {
            ...document.signature,
            signedBy: isPaid ? 'advisor' : document.signature.signedBy,
          },
        },
        docsName
      )
      if (getToggles().ENABLE_CITIZEN_ID_CONSENT_FORM) {
        yield [
          put(setAppValue(`signedDocs.${document.signature.signedBy}`, docsName)),
          take('SAVE_APPLICATION_SUCCESS'),
        ]
      }
    } else {
      const { isPayer } = app.insured
      const newDcaDocumentPayload = {
        ...document,
        person: !isPayer ? 'payer' : document.person,
        signature: {
          signedBy: !isPayer ? 'payer' : document.signature.signedBy,
        },
      }

      const isNewDcaRequirement = !isPayer && document.name === 'dca'
      const signDocumentDcaPayload = isNewDcaRequirement ? newDcaDocumentPayload : document

      yield call(signDocument, user, signDocumentDcaPayload)
    }

    yield call(onListDocument, service, { payload: { isFetch: true } })
  } catch (err) {
    console.error(err)
  } finally {
    yield [put(setAppValue('clientSignature', null)), take('SAVE_APPLICATION_SUCCESS')]
    yield put(hideLoading())
  }
}

export const onSignEmptySignatureDocument = function*(service, document) {
  const { signEmptySignatureDocument } = service
  const user = yield select(getIdentityUser)

  try {
    yield put.resolve(showLoading())
    yield put(hideDialog())
    yield call(signEmptySignatureDocument, user, document)
    yield call(onListDocument, service, { payload: { isFetch: true } })
  } catch (err) {
    console.error(err)
  } finally {
    yield put(hideLoading())
  }
}

function* getPDFReceiptDocument(service, data) {
  const { getDocument, createDownloadEcbrFile } = service
  const { user, receiptNo, policyId, document } = data
  try {
    return yield call(getDocument, user, document)
  } catch (error) {
    return yield call(createDownloadEcbrFile, user, receiptNo, policyId)
  }
}

export const onPreviewPDF = function*(service, action) {
  const { getPayment, getDocument, previewDocument, previewPayment } = service

  const user = yield select(getIdentityUser)

  const document = action.payload

  yield put.resolve(showLoading())

  if (isCordova) {
    try {
      let status

      if (isReceipt(document)) {
        const app = yield select(getCurrentApp)
        const receipt = getReceiptFromDocument(app, document)
        const receiptNo = getReceiptNo(receipt)
        const payment = {
          ...document,
          receiptNo,
        }
        status = yield call(previewPayment, user, payment)
      } else {
        status = yield call(previewDocument, user, document)
      }

      if (status === 'sign' && !document.readOnly) {
        if (isAcknowledge(document.sectionId)) {
          yield call(onSignEmptySignatureDocument, service, document)
          if (getToggles().ENABLE_CITIZEN_ID_CONSENT_FORM) {
            const docsName = document.allDocuments.map((d) => d.name)
            yield [put(setAppValue('signedDocs.acknowledge', docsName)), take('SAVE_APPLICATION_SUCCESS')]
          }
        } else {
          yield put(showSignatureDialog(document))
        }
      }
    } catch (err) {
      console.error(err)
      yield put(showErrorDialog('failed'))
    } finally {
      yield put(hideLoading())
    }
  } else {
    try {
      if (isReceipt(document)) {
        const app = yield select(getCurrentApp)
        const receipt = getReceiptFromDocument(app, document)
        const receiptNo = getReceiptNo(receipt)
        const payment = {
          ...document,
          receiptNo,
        }
        const response = yield call(getPayment, user, payment)
        yield put(addFile(document.name, response))
      } else if (_.get(document, 'name', '') === 'payment-receipt') {
        const app = yield select(getCurrentApp)
        const receiptData = app?.receipts?.find((receipt) => receipt?.receiptNo && receipt?.signed && receipt?.paid)
        const receiptNo = getReceiptNo(receiptData)
        console.log('receiptNo', receiptNo)
        const response = yield call(getPDFReceiptDocument, service, {
          user,
          receiptNo,
          policyId: app.policyId,
          document,
        })
        yield put(addFile(document.name, response))
      } else {
        const response = yield call(getDocument, user, document)
        yield put(addFile(document.name, response))
      }

      yield put(showPreviewPDFDialog(document))
    } catch (err) {
      console.error(err)
      yield put(
        setNotificationMessage({
          message: GET_DOCUMENTS_ERROR.BODY,
          title: GET_DOCUMENTS_ERROR.TITLE,
          type: GET_DOCUMENTS_ERROR.TYPE,
        })
      )
    } finally {
      yield put(hideLoading())
    }
  }
}

export const onLoadDocument = function*(service, action) {
  const { getDocument, getPayment } = service
  let response
  const user = yield select(getIdentityUser)
  const document = action.payload
  if (isReceipt(document)) {
    const app = yield select(getCurrentApp)
    const receipt = getReceiptFromDocument(app, document)
    const receiptNo = getReceiptNo(receipt)
    const payment = {
      ...document,
      receiptNo,
    }
    response = yield call(getPayment, user, payment)
  } else {
    response = yield call(getDocument, user, document)
  }
  yield put(addFile(document.name, response))
}

const getSignatureDocument = function*(service, document, index, docLength) {
  const user = yield select(getIdentityUser)
  let uuid = generateUUID()
  const { getBase64Document, getBase64Payment } = service
  let response
  if (isReceipt(document)) {
    const app = yield select(getCurrentApp)
    const paidReceipts = getPaidReceipts(app)
    const receipt = paidReceipts.length > 0 ? paidReceipts[0] : undefined
    const receiptNo = getReceiptNo(receipt)
    const payment = {
      ...document,
      receiptNo,
    }
    response = yield call(getBase64Payment, user, payment)
  } else {
    response = yield call(getBase64Document, user, document)
  }

  const docData = {
    code: response.code,
    name: document.shortText || document.text,
    data: response.data,
  }
  const payloadData = {
    needSignature: true,
    currentDocument: index + 1,
    maximumDocuments: docLength,
    messageID: uuid,
    documents: docData,
  }
  return payloadData
}

export const onPreviewMultiplePDF = function*(service, action) {
  const { getDocument, previewDocument, previewPayment, getPayment } = service

  const user = yield select(getIdentityUser)

  const app = yield select(getCurrentApp)
  let policyId = getPolicyId(app)
  if (!policyId) return
  const document = action.payload

  yield put.resolve(showLoading())

  if (isCordova && !getToggles().ENABLE_ONLY_WEBVIEW_FOR_DOCUMENT_PDF) {
    try {
      let status
      if (isReceipt(document)) {
        const app = yield select(getCurrentApp)
        const receipt = getReceiptFromDocument(app, document)
        const receiptNo = getReceiptNo(receipt)
        const payment = {
          ...document,
          receiptNo,
        }
        status = yield call(previewPayment, user, payment)
      } else {
        status = yield call(previewDocument, user, document)
      }

      if (status === 'sign' && !document.readOnly) {
        if (isAcknowledge(document.sectionId)) {
          yield call(onSignEmptySignatureDocument, service, document)
          if (getToggles().ENABLE_CITIZEN_ID_CONSENT_FORM) {
            const docsName = document.allDocuments.map((d) => d.name)
            yield [put(setAppValue('signedDocs.acknowledge', docsName)), take('SAVE_APPLICATION_SUCCESS')]
          }
        } else {
          yield put(showSignatureDialog(document))
        }
      }
    } catch (err) {
      console.error(err)
      yield put(showErrorDialog('failed'))
    } finally {
      yield put(hideLoading())
    }
  } else {
    try {
      let response
      if (isReceipt(document)) {
        const app = yield select(getCurrentApp)
        const receipt = getReceiptFromDocument(app, document)
        const receiptNo = getReceiptNo(receipt)
        const payment = {
          ...document,
          receiptNo,
        }
        response = yield call(getPayment, user, payment)
      } else {
        response = yield call(getDocument, user, document)
      }
      yield put(addFile(document.name, response))
      yield put(showPreviewMultiplePDFDialog(document))
    } catch (err) {
      console.error(err)
    } finally {
      yield put(hideLoading())
    }
  }
}

export const onCapturePhoto = function*(service, action) {
  const { deleteAdditionalDocument } = service

  const user = yield select(getIdentityUser)
  const app = yield select(getCurrentApp)
  let imageName = action.name

  if (isNil(imageName)) {
    return
  }

  try {
    yield put.resolve(showLoading())

    if (isAdditionalDocument(imageName) || isCreditCardRelationshipDocument(imageName)) {
      imageName = yield call(onCreateAdditionalDocument, service, imageName)
    }

    const base64 = yield call(applicationCordovaWrapper.openCamera, app, imageName)
    const base64Image = getOr('', 'image', base64)
    const blob = yield call(base64ToBlob, base64Image, 'image/png')

    let File = window.File
    if ('File_native' in window) {
      File = window['File_native']
    }

    const file = blob ? new File([blob], imageName, { type: 'image/png' }) : {}

    const attachment = {
      file,
      name: imageName,
    }

    yield call(uploadAttachment, service, attachment)
    yield [put({ type: 'FETCH_CURRENT_APP' }), take('LOAD_APP')]
    if (action.isShowPreview) {
      yield [
        put(previewPhoto(imageName)),
        race({
          success: take('ADD_FILE'),
          fail: take('SHOW_DIALOG'),
        }),
      ]
    }

    if (isAdditionalDocument(imageName) || isCreditCardRelationshipDocument(imageName)) {
      const files = yield select(getFiles)
      const { description, documentId } = getOr({}, action.name, files)

      yield put(
        addFile(imageName, true, {
          documentId,
          description,
          creationdate: Date.now(),
        })
      )
      yield put(
        addFileProps(action.name, {
          description: '',
        })
      )
    }
  } catch (err) {
    console.error(err)
    yield put(addError(action.name, err))
    try {
      if (isAdditionalDocument(imageName) || isCreditCardRelationshipDocument(imageName)) {
        const policyId = getPolicyId(app)
        const files = yield select(getFiles)
        const { documentId } = get(action.name, files)

        yield call(
          deleteAdditionalDocument,
          user,
          {
            documentId,
            policyId,
          },
          imageName
        )
      }
    } catch (err) {}
  } finally {
    yield put(hideLoading())
  }
}

export const onBrowsePhoto = function*(service, action) {
  // Limit photo size
  const limit_in_mb = getAppConfig().MAXIMUM_FILE_SIZE_LIMIT_IN_MB
  const limit = limit_in_mb * 1024 * 1024
  const image = action.payload
  const imageSize = getOr(0, 'size', image)
  let imageName = action.name
  let documentType = action.name
  const format = action.format

  if (
    (!AVAILABLE_IMAGE_FORMATS.includes(format) || !AVAILABLE_IMAGE_FORMATS.includes(image.type)) &&
    !AVAILABLE_FILE_FORMATS.includes(image.type)
  ) {
    yield put(addError(action.name, 'INVALID_IMAGE_FILE'))
    return
  }

  if (imageSize > limit) {
    yield put(addError(action.name, 'FILE_SIZE_OVER_LIMIT'))
    return
  }

  if (isNil(documentType) || isNil(image)) {
    return
  }

  try {
    if (!action.ocr) {
      yield put.resolve(showLoading())
    }

    if (isAdditionalDocument(documentType) || isCreditCardRelationshipDocument(documentType)) {
      imageName = yield call(onCreateAdditionalDocument, service, documentType)
    } else if (isDaRelationshipDocument(documentType)) {
      if (getToggles().ENABLE_EDA) {
        imageName = yield call(onCreateDaRelationshipDocument, service, documentType)
      }
    }

    let attachment = {
      file: image,
      name: imageName,
    }
    yield call(uploadAttachment, service, attachment)

    yield [put({ type: 'FETCH_CURRENT_APP' }), take('LOAD_APP')]

    if (isPaymentSlipDocument(imageName)) {
      yield put(setAppValue('isPaymentEvidenceUpdated', true))
    }

    yield put(removeError(action.name))

    if (action.isShowPreview) {
      if (isCordova) {
        yield put(addFile(imageName, image))
      } else {
        yield [
          put(previewPhoto(imageName)),
          race({
            success: take('ADD_FILE'),
            fail: take('SHOW_DIALOG'),
          }),
        ]
      }
    }

    if (isAdditionalDocument(imageName) || isCreditCardRelationshipDocument(imageName)) {
      const files = yield select(getFiles)
      const { description, documentId } = getOr({}, action.name, files)

      yield put(
        addFile(imageName, true, {
          documentId,
          description,
          creationdate: Date.now(),
        })
      )
      yield put(
        addFileProps(action.name, {
          description: '',
        })
      )
    }

    attachment = null
  } catch (err) {
    console.error(err)
    yield put(addError(imageName, err))
  } finally {
    if (!action.ocr) {
      yield put(hideLoading())
    }
  }
}

export const onClonePhoto = function*(service, action) {
  const { getAttachment } = service

  const user = yield select(getIdentityUser)
  const app = yield select(getCurrentApp)
  const { from, destination } = action
  try {
    yield put.resolve(showLoading())

    let [image] = yield [call(getAttachment, app, from, user), delay(1000)]
    const attachment = {
      file: image,
      name: destination,
    }
    yield call(uploadAttachment, service, attachment)

    yield [put({ type: 'FETCH_CURRENT_APP' }), take('LOAD_APP')]
  } catch (err) {
    console.error(err)
  } finally {
    yield put(hideLoading())
  }
}

export const getUnsignedDocImage = function*(service, action) {
  const { getUnsignedDocAttachment } = service

  const user = yield select(getIdentityUser)
  const app = yield select(getCurrentApp)
  const policyId = getPolicyId(app)
  const imageName = action.name

  if (isNil(imageName)) {
    return
  }

  try {
    let [image] = yield [call(getUnsignedDocAttachment, policyId, imageName, user)]

    if (isString(image)) {
      image = 'data:image/jpeg;base64,' + image
    }

    yield put(addFile(imageName, image))
  } catch (err) {
    console.error('ERROR getUnsignedDocImage : ', err)
  } finally {
    yield put(hideLoading())
  }
}

export const onPreviewPhoto = function*(service, action) {
  const { getAttachment } = service

  const user = yield select(getIdentityUser)
  const app = yield select(getCurrentApp)
  const imageName = action.name

  if (isNil(imageName)) {
    return
  }

  try {
    yield put.resolve(showLoading())

    let [image] = yield [call(getAttachment, app, imageName, user), delay(1000)]
    if (isString(image)) {
      image = 'data:image/jpeg;base64,' + image
    }

    yield put(addFile(imageName, image))
  } catch (err) {
    console.error(err)
  } finally {
    yield put(hideLoading())
  }
}

export const onDeletePhoto = function*(service, action) {
  const { deleteAdditionalDocument } = service
  const imageName = action.name

  const user = yield select(getIdentityUser)
  const app = yield select(getCurrentApp)
  const attachments = yield select(getCurrentValue, '_attachments')
  const newAttachments = omit(imageName, attachments)

  try {
    yield put.resolve(showLoading())
    const documentType = isCreditCardRelationshipDocument(imageName)
      ? DOCUMENT_TYPE['insured']['CREDIT_CARD_RELATIONSHIP_DOCUMENT']['name']
      : DOCUMENT_TYPE['insured']['ADDITIONAL_DOCUMENT']['name']

    if (isAdditionalDocument(imageName) || isCreditCardRelationshipDocument(imageName)) {
      const policyId = getPolicyId(app)
      const files = yield select(getFiles)
      const { documentId } = get(imageName, files)

      yield call(
        deleteAdditionalDocument,
        user,
        {
          documentId,
          policyId,
        },
        documentType
      )

      yield put(removeFile(imageName))
    }

    yield [put(setAppValue('_attachments', newAttachments)), take('SAVE_APPLICATION_SUCCESS')]

    yield [put(setAppValue('isEvidenceUploaded', false))]
  } catch (err) {
    console.error(err)
  } finally {
    yield put(hideLoading())
  }
}

export const onCreateAdditionalDocument = function*(service, documentName, selectedRemoteSelling) {
  const { createAdditionalDocument } = service
  const files = yield select(getFiles)

  if (!has(documentName, files) && !selectedRemoteSelling) {
    return documentName // additional-docs
  }
  const { description } = getOr({}, documentName, files)

  const user = yield select(getIdentityUser)
  const app = yield select(getCurrentApp)
  const policyId = getPolicyId(app)

  const { documentId } = yield call(
    createAdditionalDocument,
    user,
    {
      policyId,
      description: description || '',
    },
    documentName
  )
  const attachmentName = `${documentName}_${documentId}`
  yield put(
    addFileProps(selectedRemoteSelling ? attachmentName : documentName, {
      documentId,
      isInsured: selectedRemoteSelling,
    })
  )

  return attachmentName
}

export const onCreateDaRelationshipDocument = function*(service, documentName, selectedRemoteSelling) {
  const { createAdditionalDocument } = service

  const user = yield select(getIdentityUser)
  const app = yield select(getCurrentApp)
  const policyId = getPolicyId(app)

  const { documentId } = yield call(
    createAdditionalDocument,
    user,
    {
      policyId,
      description: 'เอกสารพิสูจน์ความสัมพันธ์',
    },
    documentName
  )

  yield put(
    addFileProps(documentName, {
      documentId,
      isInsured: selectedRemoteSelling,
    })
  )

  return documentName
}

export const onSaveAuraSubmitDataToMiddelware = function*(service, action) {
  const { getDataAuraMiddleware } = service

  const user = yield select(getIdentityUser)
  const app = yield select(getCurrentApp)
  const { payload } = action
  const { applicationId } = app

  if (isNil(payload)) {
    return
  }

  try {
    yield put.resolve(showLoading())
    const { insured, payer } = payload
    const resultGetDataState = yield call(getDataAuraMiddleware, user, applicationId)

    const prepAuraObj = {
      dataInsured: {
        ...resultGetDataState.insured,
        ...insured,
      },
      dataPayer: {
        ...resultGetDataState.payer,
        ...payer,
      },
    }

    if (!_.isEmpty(prepAuraObj.dataInsured)) {
      yield put(setAppValue('insured', prepAuraObj.dataInsured))
    }

    if (!_.isEmpty(prepAuraObj.dataPayer)) {
      yield put(setAppValue('payer', prepAuraObj.dataPayer))
    }
  } catch (err) {
    console.error(err)
  } finally {
    yield put(hideLoading())
  }
}

export const onSaveEcbrPhoto = function*(service, action) {
  const { getAttachment } = service

  const user = yield select(getIdentityUser)
  const app = yield select(getCurrentApp)
  const { from, destination } = action

  let uploadSuccess = false
  try {
    yield put.resolve(showLoading())
    let [image] = yield [call(getAttachment, app, from, user), delay(1000)]
    const attachment = {
      file: image,
      name: destination,
    }
    yield [call(uploadAttachment, service, attachment), delay(1000)]
    yield [put({ type: 'FETCH_CURRENT_APP' }), take('LOAD_APP')]

    const attachments = yield select(getCurrentValue, '_attachments')
    const newAttachments = _.omit(attachments, from)
    yield put(removeFile(from))
    yield put(setAppValue('_attachments', newAttachments))
    yield [put({ type: 'FETCH_CURRENT_APP' }), take('LOAD_APP')]
    uploadSuccess = true
  } catch (err) {
    console.error(err)
  } finally {
    yield put(hideLoading())
    if (uploadSuccess) {
      yield put({ type: 'ECBR_PHOTO_SUCCESS' })
    }
  }
}

export const onUpdateHealthSession = function*(service, action) {
  const { payload } = action
  yield put(setAppValue('healthSession', payload))
}

export default function*(service) {
  yield all([
    takeLatest('FETCH_SIGNATURE', onListDocument, service),
    takeLatest('SHOW_SIGNPAD', onShowSignPad, service),
    takeLatest('SIGN_DOCUMENT', onSignDocument, service),
    takeLatest('PREVIEW_PDF', onPreviewPDF, service),
    takeLatest('PREVIEW_MULTIPLE_PDF', onPreviewMultiplePDF, service),
    takeLatest('CAPTURE_PHOTO', onCapturePhoto, service),
    takeEvery('BROWSE_PHOTO', onBrowsePhoto, service),
    takeLatest('CLONE_PHOTO', onClonePhoto, service),
    takeLatest('PREVIEW_PHOTO', onPreviewPhoto, service),
    takeLatest('GET_UNSIGNED_DOC_IMG', getUnsignedDocImage, service),
    takeLatest('DELETE_PHOTO', onDeletePhoto, service),
    takeLatest('LOAD_DOCUMENT', onLoadDocument, service),
    takeLatest('UPLOAD_ATTACHMENT', uploadAttachment, service),
    takeLatest('SAVE_DATA_AURA_TO_MIDDLEWARE', onSaveAuraSubmitDataToMiddelware, service),
    takeLatest('SAVE_ECBR_PHOTO', onSaveEcbrPhoto, service),
    takeLatest('UPDATE_HEALTH_SESSION', onUpdateHealthSession, service),
  ])
}
